import { API_BASE, getCsrfToken } from '../config';
import React, { useState, useEffect } from 'react';
import ModernPageLayout, { ModernCard, ModernBadge, colorPalette } from './ModernPageLayout';
import { GraduationCap, BookOpen, FileCheck, Plus, CheckCircle, XCircle, Calendar, Users, MapPin, Clock, DollarSign, Home, ArrowLeft, PanelRightOpen, X } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';
import LanguageSelector from './LanguageSelector';

const FormationsManagement = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('catalogue');
  const [formations, setFormations] = useState([]);
  const [inscriptions, setInscriptions] = useState([]);
  const [pendingInscriptions, setPendingInscriptions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [editingFormation, setEditingFormation] = useState(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);
  const [formationsDrawerOpen, setFormationsDrawerOpen] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const check = () => {
      setIsMobile(mql.matches);
      if (!mql.matches) setFormationsDrawerOpen(false);
    };
    check();
    mql.addEventListener('change', check);
    return () => mql.removeEventListener('change', check);
  }, []);

  // Formulaire création/modification formation
  const [newFormation, setNewFormation] = useState({
    title: '',
    description: '',
    category: '',
    duration_hours: '',
    max_participants: '',
    location: '',
    trainer: '',
    start_date: '',
    end_date: '',
    cost_per_person: '',
    prerequisites: '',
    objectives: '',
    is_mandatory: false,
    target_departments: [],
    registration_deadline: '',
    external_provider: '',
    certification_provided: false,
    target_audience: 'all'
  });

  const categories = [
    t('formations.category_security'),
    t('formations.category_office'),
    t('formations.category_management'),
    t('formations.category_technical'),
    t('formations.category_sales'),
    t('formations.category_hr'),
    t('formations.category_accounting'),
    t('formations.category_languages'),
    t('formations.category_personal_dev'),
    t('formations.category_regulatory')
  ];

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${API_BASE}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => setUser(data.user))
      .catch(console.error);
    }
  }, []);

  // Load pending count for Validation badge (admin/manager) as soon as user is known
  useEffect(() => {
    if (user && ['admin', 'manager'].includes(user.role)) {
      loadPendingInscriptions(true);
    }
  }, [user?.id, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'catalogue' || (activeTab === 'inscrits' && ['admin', 'manager'].includes(user?.role))) {
      loadFormations();
    } else if (activeTab === 'mes-inscriptions') {
      loadMesInscriptions();
    } else if (activeTab === 'validation' && ['admin', 'manager'].includes(user?.role)) {
      loadPendingInscriptions();
    }
  }, [activeTab, user, showArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFormations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/formations/catalogue?archived=${showArchived}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error(t('formations.non_json_response'));
      }
      
      const data = await response.json();
      setFormations(data.formations || []);
      setDepartments(data.departments || []);
      // Marquer les formations comme vues (badge count dynamique)
      if (token) {
        (async () => {
          const csrf = await getCsrfToken();
          if (csrf) {
            fetch(`${API_BASE}/api/formations/mark-catalogue-viewed`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}`, 'X-CSRF-Token': csrf }
            }).catch(() => {});
          }
        })();
      }
    } catch (error) {
      console.error('Erreur chargement formations:', error);
    }
    setLoading(false);
  };

  const loadMesInscriptions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/formations/mes-inscriptions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error(t('formations.non_json_response'));
      }
      
      const data = await response.json();
      setInscriptions(data.inscriptions || []);
    } catch (error) {
      console.error('Erreur chargement inscriptions:', error);
    }
    setLoading(false);
  };

  const loadPendingInscriptions = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/formations/inscriptions/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error(t('formations.non_json_response'));
      }
      
      const data = await response.json();
      setPendingInscriptions(data.inscriptions || []);
    } catch (error) {
      console.error('Erreur chargement inscriptions en attente:', error);
    }
    if (!silent) setLoading(false);
  };

  const resetForm = () => {
    setNewFormation({
      title: '',
      description: '',
      category: '',
      duration_hours: '',
      max_participants: '',
      location: '',
      trainer: '',
      start_date: '',
      end_date: '',
      cost_per_person: '',
      prerequisites: '',
      objectives: '',
      is_mandatory: false,
      target_departments: [],
      registration_deadline: '',
      external_provider: '',
      certification_provided: false,
      target_audience: 'all'
    });
    setEditingFormation(null);
  };

  const handleCreateFormation = async () => {
    try {
      // Vérification des champs requis avant l'envoi
      if (!newFormation.title || !newFormation.description || !newFormation.category) {
        alert(`❌ ${t('formations.error_required_fields') || 'Veuillez remplir tous les champs obligatoires (Titre, Description, Catégorie)'}`);
        return;
      }

      // Admin ou Manager peuvent créer
      if (!user || !['admin', 'manager'].includes(user.role)) {
        alert(`❌ ${t('formations.error_admin_only') || 'Seuls les administrateurs et managers peuvent créer des formations'}`);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        alert(`❌ ${t('auth.token_missing') || 'Vous devez être connecté'}`);
        return;
      }
      
      const formationData = {
        ...newFormation,
        target_departments: newFormation.target_departments.length > 0 
          ? newFormation.target_departments.join(',') 
          : null
      };

      const csrf = await getCsrfToken();
      if (!csrf) {
        alert('❌ ' + (t('formations.error_operation') || 'Please refresh the page and try again.'));
        return;
      }
      const url = editingFormation 
        ? `${API_BASE}/api/formations/catalogue/${editingFormation.id}`
        : `${API_BASE}/api/formations/catalogue`;
      
      const method = editingFormation ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-CSRF-Token': csrf
        },
        body: JSON.stringify(formationData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        const errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
        console.error('Erreur API:', errorMessage, errorData);
        throw new Error(errorMessage);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error(t('formations.non_json_response'));
      }

      const data = await response.json();
      
      if (response.ok) {
        alert('✅ ' + (data.message || t('formations.success_created') || 'Formation créée avec succès'));
        resetForm();
        setActiveTab('catalogue');
        loadFormations();
      } else {
        alert('❌ ' + (data.error || t('formations.error_operation')));
      }
    } catch (error) {
      console.error('Erreur création/modification formation:', error);
      const errorMessage = error.message || t('formations.error_operation');
      alert('❌ ' + errorMessage);
    }
  };

  const handleEditFormation = (formation) => {
    setNewFormation({
      title: formation.title || '',
      description: formation.description || '',
      category: formation.category || '',
      duration_hours: formation.duration_hours || '',
      max_participants: formation.max_participants || '',
      location: formation.location || '',
      trainer: formation.trainer || '',
      start_date: formation.start_date ? new Date(formation.start_date).toISOString().slice(0, 16) : '',
      end_date: formation.end_date ? new Date(formation.end_date).toISOString().slice(0, 16) : '',
      cost_per_person: formation.cost_per_person || '',
      prerequisites: formation.prerequisites || '',
      objectives: formation.objectives || '',
      is_mandatory: formation.is_mandatory || false,
      target_departments: formation.target_departments ? formation.target_departments.split(',').map(d => d.trim()) : [],
      registration_deadline: formation.registration_deadline ? new Date(formation.registration_deadline).toISOString().slice(0, 16) : '',
      external_provider: formation.external_provider || '',
      certification_provided: formation.certification_provided || false,
      target_audience: formation.target_audience || 'all'
    });
    setEditingFormation(formation);
    setActiveTab('creer-formation');
  };

  const handleDeleteFormation = async (formationId) => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm(t('formations.delete_confirm'))) {
      try {
        const token = localStorage.getItem('token');
        const csrf = await getCsrfToken();
        if (!csrf) {
          alert('❌ ' + (t('formations.error_operation') || 'Please refresh the page and try again.'));
          return;
        }
        const response = await fetch(`${API_BASE}/api/formations/catalogue/${formationId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}`, 'X-CSRF-Token': csrf }
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
          throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }
        
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error(t('formations.non_json_response'));
        }

        const data = await response.json();
        
        if (response.ok) {
          alert('✅ ' + data.message);
          loadFormations();
        } else {
          alert('❌ ' + data.error);
        }
      } catch (error) {
        console.error('Erreur suppression formation:', error);
        alert('❌ ' + (error.message || t('formations.error_delete')));
      }
    }
  };

  const handleArchiveFormation = async (formationId, archive = true) => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm(archive ? t('formations.archive_confirm') : t('formations.reactivate_confirm'))) {
      try {
        const token = localStorage.getItem('token');
        const csrf = await getCsrfToken();
        if (!csrf) {
          alert('❌ ' + (t('formations.error_operation') || 'Please refresh the page and try again.'));
          return;
        }
        const response = await fetch(`${API_BASE}/api/formations/${formationId}/archive`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'X-CSRF-Token': csrf
          },
          body: JSON.stringify({ archived: archive })
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
          throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }
        
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error(t('formations.non_json_response'));
        }

        const data = await response.json();
        
        if (response.ok) {
          alert('✅ ' + data.message);
          loadFormations();
        } else {
          alert('❌ ' + data.error);
        }
      } catch (error) {
        console.error('Erreur archivage formation:', error);
        alert('❌ ' + (error.message || t('formations.error_operation')));
      }
    }
  };

  const handleInscription = async (trainingId, justification) => {
    try {
      const token = localStorage.getItem('token');
      const csrf = await getCsrfToken();
      if (!csrf) {
        alert('❌ ' + (t('formations.error_inscription') || 'Please refresh the page and try again.'));
        return;
      }
      const response = await fetch(`${API_BASE}/api/formations/inscriptions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-CSRF-Token': csrf
        },
        body: JSON.stringify({
          training_id: trainingId,
          justification
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        alert('✅ ' + data.message);
        loadFormations();
      } else {
        alert('❌ ' + data.error);
      }
    } catch (error) {
      alert('❌ ' + t('formations.error_inscription'));
    }
  };

  const handleValidation = async (inscriptionId, action, reason = '') => {
    try {
      const token = localStorage.getItem('token');
      const csrf = await getCsrfToken();
      if (!csrf) {
        alert('❌ ' + (t('formations.error_operation') || 'Please refresh the page and try again.'));
        return;
      }
      const response = await fetch(`${API_BASE}/api/formations/inscriptions/${inscriptionId}/validate`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-CSRF-Token': csrf
        },
        body: JSON.stringify({
          action,
          rejection_reason: reason
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error(t('formations.non_json_response'));
      }

      const data = await response.json();
      
      if (response.ok) {
        alert('✅ ' + data.message);
        loadPendingInscriptions();
      } else {
        alert('❌ ' + data.error);
      }
    } catch (error) {
      console.error('Erreur validation inscription:', error);
      alert('❌ ' + (error.message || t('formations.error_validation')));
    }
  };

  const handleDepartmentToggle = (department) => {
    setNewFormation(prev => ({
      ...prev,
      target_departments: prev.target_departments.includes(department)
        ? prev.target_departments.filter(d => d !== department)
        : [...prev.target_departments, department]
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f59e0b';
      case 'approved': return '#10b981';
      case 'rejected': return '#ef4444';
      case 'completed': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return t('formations.status_pending') || 'En attente';
      case 'approved': return t('formations.status_approved') || 'Approuvée';
      case 'rejected': return t('formations.status_rejected') || 'Rejetée';
      case 'completed': return t('formations.status_completed') || 'Terminée';
      default: return status;
    }
  };

  const isFormValid = newFormation.title && newFormation.description && newFormation.category;

  // Styles responsives intégrés
  const mobileStyles = {
    container: {
      minHeight: '100vh',
      background: '#f9fafb',
      padding: isMobile ? '10px' : '24px'
    },
    wrapper: {
      maxWidth: '1536px',
      margin: '0 auto',
      width: '100%'
    },
    header: {
      marginBottom: isMobile ? '20px' : '32px'
    },
    title: {
      fontSize: isMobile ? '1.5rem' : '1.875rem',
      fontWeight: 'bold',
      color: '#111827',
      marginBottom: '8px',
      textAlign: isMobile ? 'center' : 'left'
    },
    subtitle: {
      color: '#6b7280',
      textAlign: isMobile ? 'center' : 'left'
    },
    tabsContainer: {
      background: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      marginBottom: isMobile ? '20px' : '24px'
    },
    tabsWrapper: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      borderBottom: '1px solid #e5e7eb',
      gap: isMobile ? '0' : '0'
    },
    tab: (isActive) => ({
      padding: isMobile ? '12px 16px' : '12px 24px',
      fontWeight: '500',
      fontSize: isMobile ? '0.8rem' : '0.875rem',
      borderBottom: isMobile ? 'none' : `2px solid ${isActive ? '#3b82f6' : 'transparent'}`,
      color: isActive ? '#2563eb' : '#6b7280',
      cursor: 'pointer',
      transition: 'colors 0.2s',
      textAlign: isMobile ? 'center' : 'left',
      borderRight: isMobile ? '1px solid #e5e7eb' : 'none'
    }),
    contentCard: {
      background: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      overflow: 'hidden'
    },
    cardHeader: {
      padding: isMobile ? '16px' : '24px',
      borderBottom: '1px solid #e5e7eb'
    },
    cardTitle: {
      fontSize: isMobile ? '1.1rem' : '1.25rem',
      fontWeight: '600',
      color: '#111827'
    },
    // Table responsive avec scroll horizontal
    tableWrapper: {
      width: '100%',
      overflowX: isMobile ? 'auto' : 'visible',
      WebkitOverflowScrolling: 'touch'
    },
    table: {
      minWidth: isMobile ? '1000px' : '100%',
      width: isMobile ? 'max-content' : '100%',
      borderCollapse: 'collapse'
    },
    th: {
      padding: isMobile ? '8px 12px' : '12px 24px',
      textAlign: 'left',
      fontSize: isMobile ? '0.7rem' : '0.75rem',
      fontWeight: '500',
      color: '#6b7280',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      background: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
      whiteSpace: 'nowrap'
    },
    td: {
      padding: isMobile ? '8px 12px' : '16px 24px',
      borderBottom: '1px solid #e5e7eb',
      verticalAlign: 'top',
      fontSize: isMobile ? '0.8rem' : '0.875rem'
    },
    // Cartes formation pour mobile
    formationCard: {
      background: 'white',
      borderRadius: '12px',
      padding: '16px',
      marginBottom: '12px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e5e7eb'
    },
    formationTitle: {
      fontSize: '1rem',
      fontWeight: '600',
      color: '#111827',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexWrap: 'wrap'
    },
    formationDescription: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginBottom: '12px',
      lineHeight: '1.4'
    },
    formationMeta: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '8px',
      marginBottom: '12px',
      fontSize: '0.75rem',
      color: '#6b7280'
    },
    formationActions: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    badge: (color, bgColor) => ({
      padding: '4px 8px',
      fontSize: '0.7rem',
      fontWeight: '500',
      borderRadius: '12px',
      background: bgColor,
      color: color,
      whiteSpace: 'nowrap'
    }),
    button: (variant = 'primary') => {
      const variants = {
        primary: { bg: '#3b82f6', hover: '#2563eb', text: 'white' },
        secondary: { bg: '#f59e0b', hover: '#d97706', text: 'white' },
        danger: { bg: '#ef4444', hover: '#dc2626', text: 'white' },
        success: { bg: '#10b981', hover: '#059669', text: 'white' },
        gray: { bg: '#6b7280', hover: '#4b5563', text: 'white' }
      };
      
      return {
        padding: isMobile ? '8px 12px' : '6px 12px',
        fontSize: isMobile ? '0.8rem' : '0.75rem',
        fontWeight: '500',
        borderRadius: '6px',
        border: 'none',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        background: variants[variant].bg,
        color: variants[variant].text,
        textAlign: 'center',
        width: isMobile ? '100%' : 'auto'
      };
    },
    // Formulaire responsive
    formGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
      gap: isMobile ? '16px' : '24px'
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column'
    },
    label: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '8px'
    },
    input: {
      width: '100%',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      padding: isMobile ? '10px' : '8px 12px',
      fontSize: isMobile ? '16px' : '0.875rem', // 16px évite le zoom iOS
      transition: 'border-color 0.2s, box-shadow 0.2s',
      boxSizing: 'border-box'
    },
    textarea: {
      width: '100%',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      padding: isMobile ? '10px' : '8px 12px',
      fontSize: isMobile ? '16px' : '0.875rem',
      resize: 'vertical',
      minHeight: '80px',
      boxSizing: 'border-box'
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('home.good_morning');
    if (hour < 18) return t('home.good_afternoon');
    return t('home.good_evening');
  };

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto'
    }}>
      {/* Header: mobile = compact bar + drawer; desktop = full title */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: isMobile ? '12px 16px' : '24px 20px 20px',
        background: isMobile ? 'rgba(10, 14, 39, 0.85)' : 'transparent',
        backdropFilter: isMobile ? 'blur(20px)' : 'none',
        borderBottom: isMobile ? '1px solid rgba(255,255,255,0.1)' : 'none',
        zIndex: 999,
        maxWidth: '100%'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: isMobile ? 0 : '6px',
          gap: '16px',
          minWidth: 0
        }}>
          {onNavigate && (
            <button
              onClick={() => onNavigate('home')}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '10px',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                backdropFilter: 'blur(10px)',
                flexShrink: 0,
                width: '44px',
                height: '44px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title={t('common.back_to_home')}
            >
              <Home size={20} />
            </button>
          )}
          {isMobile ? (
            <>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#fff', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <GraduationCap size={22} style={{ flexShrink: 0 }} />
                {t('formations.title')}
              </div>
              <button
                type="button"
                onClick={() => setFormationsDrawerOpen(true)}
                aria-label={t('home.open_menu') || 'Menu'}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#fff',
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
              >
                <PanelRightOpen size={20} />
              </button>
            </>
          ) : (
            <div style={{ fontSize: 'clamp(28px, 5vw, 40px)', fontWeight: 700, maxWidth: '100%', letterSpacing: '-0.5px', lineHeight: 1.2, flex: 1 }}>
              🎓 {t('formations.title')}
            </div>
          )}
        </div>
      </div>

      {/* Mobile drawer */}
      {isMobile && formationsDrawerOpen && (
        <div role="presentation" onClick={() => setFormationsDrawerOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', justifyContent: 'flex-end' }}>
          <div role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} style={{ width: 'min(320px, 88vw)', height: '100%', background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)', padding: '20px 16px', boxSizing: 'border-box', overflowY: 'auto', borderLeft: '1px solid rgba(255,255,255,0.12)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>{t('formations.title')}</span>
              <button type="button" onClick={() => setFormationsDrawerOpen(false)} aria-label={t('common.close') || 'Close'} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', width: 40, height: 40, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.8)', marginBottom: 16 }}>🎓 {t('formations.title')}</p>
            <div>
              <LanguageSelector style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', padding: '6px 10px', borderRadius: 10 }} />
            </div>
          </div>
        </div>
      )}

      {/* Contenu principal */}
      <div className="formations-content" style={{
        paddingTop: isMobile ? '88px' : '100px',
        paddingBottom: '40px',
        paddingLeft: '20px',
        paddingRight: '20px',
        maxWidth: '1600px',
        margin: '0 auto'
      }}>
        {/* Navigation Tabs modernes */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '24px',
        flexWrap: 'wrap',
        overflowX: 'auto',
        paddingBottom: '8px'
      }}>
        <button
          onClick={() => setActiveTab('catalogue')}
          style={{
            padding: '12px 24px',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: 600,
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            background: activeTab === 'catalogue'
              ? colorPalette.formations.gradient
              : 'rgba(255, 255, 255, 0.1)',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: activeTab === 'catalogue' ? `0 4px 16px ${colorPalette.formations.shadow}` : 'none'
          }}
        >
          <BookOpen size={18} />
          {t('formations.catalog')}
        </button>
        
        <button
          onClick={() => setActiveTab('mes-inscriptions')}
          style={{
            padding: '12px 24px',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: 600,
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            background: activeTab === 'mes-inscriptions'
              ? colorPalette.formations.gradient
              : 'rgba(255, 255, 255, 0.1)',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: activeTab === 'mes-inscriptions' ? `0 4px 16px ${colorPalette.formations.shadow}` : 'none'
          }}
        >
          <FileCheck size={18} />
          {t('formations.my_inscriptions')}
        </button>

        {['admin', 'manager'].includes(user?.role) && (
          <>
            <button
              onClick={() => {
                resetForm();
                setActiveTab('creer-formation');
              }}
              style={{
                padding: '12px 24px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                background: activeTab === 'creer-formation'
                  ? colorPalette.formations.gradient
                  : 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: activeTab === 'creer-formation' ? `0 4px 16px ${colorPalette.formations.shadow}` : 'none'
              }}
            >
              <Plus size={18} />
              {editingFormation ? t('formations.edit') : t('formations.create')}
            </button>

            <button
              onClick={() => setActiveTab('validation')}
              style={{
                padding: '12px 24px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                background: activeTab === 'validation'
                  ? colorPalette.formations.gradient
                  : 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                position: 'relative',
                boxShadow: activeTab === 'validation' ? `0 4px 16px ${colorPalette.formations.shadow}` : 'none'
              }}
            >
              <CheckCircle size={18} />
              {t('formations.validation') || 'Validation'}
              {pendingInscriptions.length > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                  background: '#ef4444',
                  color: 'white',
                  fontSize: '0.75rem',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700
                }}>
                  {pendingInscriptions.length}
                </span>
              )}
            </button>

            <button
              onClick={() => setActiveTab('inscrits')}
              style={{
                padding: '12px 24px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                background: activeTab === 'inscrits'
                  ? colorPalette.formations.gradient
                  : 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: activeTab === 'inscrits' ? `0 4px 16px ${colorPalette.formations.shadow}` : 'none'
              }}
            >
              <Users size={18} />
              {t('Registered Employees') || 'Registered Employees'}
            </button>
          </>
        )}
      </div>

      {/* Contenu avec loading */}
      {loading ? (
        <ModernCard
          gradient={colorPalette.formations.gradient}
          shadow={colorPalette.formations.shadow}
          style={{ textAlign: 'center', padding: '64px 24px' }}
        >
          <div style={{
            width: '48px',
            height: '48px',
            border: '3px solid rgba(255, 255, 255, 0.2)',
            borderTop: '3px solid #ffffff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto'
          }}></div>
        </ModernCard>
      ) : (
        <div>
          {/* CATALOGUE DES FORMATIONS */}
          {activeTab === 'catalogue' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Contrôles Admin */}
              {user?.role === 'admin' && (
                <ModernCard
                  title={t('formations.catalog_management')}
                  icon={<GraduationCap size={20} />}
                  gradient={colorPalette.formations.gradient}
                  shadow={colorPalette.formations.shadow}
                >
                  <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: isMobile ? 'stretch' : 'center',
                    gap: '16px'
                  }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      color: 'rgba(255, 255, 255, 0.8)',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={showArchived}
                        onChange={(e) => setShowArchived(e.target.checked)}
                        style={{ 
                          borderRadius: '4px',
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer'
                        }}
                      />
                      <span>{t('formations.show_archived') || 'Afficher formations archivées'}</span>
                    </label>
                    <button
                      onClick={() => {
                        resetForm();
                        setActiveTab('creer-formation');
                      }}
                      style={{
                        padding: '12px 24px',
                        borderRadius: '12px',
                        fontSize: '14px',
                        fontWeight: 600,
                        border: 'none',
                        cursor: 'pointer',
                        background: colorPalette.formations.gradient,
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.3s ease',
                        boxShadow: `0 4px 16px ${colorPalette.formations.shadow}`
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = `0 6px 20px ${colorPalette.formations.shadow}`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = `0 4px 16px ${colorPalette.formations.shadow}`;
                      }}
                    >
                      <Plus size={18} />
                      {t('formations.new_formation') || 'Nouvelle Formation'}
                    </button>
                  </div>
                </ModernCard>
              )}

              {/* Formations - Grid moderne */}
              <ModernCard
                gradient={colorPalette.formations.gradient}
                shadow={colorPalette.formations.shadow}
              >
                  {isMobile ? (
                    // Version mobile avec cardes
                    <div style={{ padding: '16px' }}>
                      {formations.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '48px 16px' }}>
                          <div style={{ fontSize: '4rem', marginBottom: '16px' }}>📚</div>
                          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                            {t('formations.no_formations')}
                          </h3>
                          <p style={{ color: '#6b7280' }}>{t('formations.catalog_update_soon')}</p>
                        </div>
                      ) : (
                        formations.map((formation) => (
                          <div key={formation.id} style={mobileStyles.formationCard}>
                            <div style={mobileStyles.formationTitle}>
                              <span>{formation.title}</span>
                              <span style={mobileStyles.badge(
                                formation.is_mandatory ? '#dc2626' : '#059669',
                                formation.is_mandatory ? '#fee2e2' : '#dcfce7'
                              )}>
                                {formation.is_mandatory ? t('formations.mandatory') : t('formations.optional')}
                              </span>
                              {!formation.is_active && (
                                <span style={mobileStyles.badge('#6b7280', '#f3f4f6')}>
                                  {t('formations.archived')}
                                </span>
                              )}
                            </div>
                            
                            <p style={mobileStyles.formationDescription}>
                              {formation.description}
                            </p>
                            
                            {formation.target_departments && (
                              <p style={{ fontSize: '0.75rem', color: '#2563eb', marginBottom: '12px' }}>
                                {t('formations.departments_label')} : {formation.target_departments}
                              </p>
                            )}
                            
                            <div style={mobileStyles.formationMeta}>
                              <div>📂 {formation.category}</div>
                              {formation.duration_hours && <div>⏱️ {formation.duration_hours}h</div>}
                              {formation.trainer && <div>👨‍🏫 {formation.trainer}</div>}
                              {formation.location && <div>📍 {formation.location}</div>}
                              {formation.start_date && (
                                <div>📅 {new Date(formation.start_date).toLocaleDateString(navigator.language || 'fr-FR')}</div>
                              )}
                              <div>
                                👥 {formation.enrolled_count || 0}
                                {formation.max_participants && ` / ${formation.max_participants}`}
                              </div>
                            </div>
                            
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '12px'
                            }}>
                              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>
                                {formation.cost_per_person > 0 ? `${formation.cost_per_person}€` : t('formations.free')}
                              </span>
                              {formation.pending_count > 0 && (
                                <span style={{ fontSize: '0.75rem', color: '#d97706' }}>
                                  {t('formations.pending_count', { count: formation.pending_count })}
                                </span>
                              )}
                            </div>
                            
                            <div style={mobileStyles.formationActions}>
                              {user?.role !== 'admin' && formation.is_active && (
                                <button
                                  onClick={() => {
                                    const justification = prompt(t('formations.justification_prompt'));
                                    if (justification) {
                                      handleInscription(formation.id, justification);
                                    }
                                  }}
                                  style={mobileStyles.button(
                                    formation.current_user_enrolled || (formation.max_participants && formation.enrolled_count >= formation.max_participants)
                                      ? 'gray' : 'primary'
                                  )}
                                  disabled={formation.current_user_enrolled || (formation.max_participants && formation.enrolled_count >= formation.max_participants)}
                                >
                                  {formation.current_user_enrolled
                                    ? `✓ ${t('formations.already_registered') || 'Déjà inscrit'}`
                                    : formation.max_participants && formation.enrolled_count >= formation.max_participants
                                    ? `🚫 ${t('formations.full') || 'Complet'}`
                                    : `📝 ${t('formations.inscribe')}`
                                  }
                                </button>
                              )}
                              
                              {['admin', 'manager'].includes(user?.role) && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                                  <button
                                    onClick={() => handleEditFormation(formation)}
                                    style={mobileStyles.button('secondary')}
                                  >
                                    ✏️ {t('common.edit')}
                                  </button>
                                  <button
                                    onClick={() => handleArchiveFormation(formation.id, formation.is_active)}
                                    style={mobileStyles.button(formation.is_active ? 'secondary' : 'success')}
                                  >
                                    {formation.is_active ? `📦 ${t('formations.archive')}` : `🔄 ${t('formations.reactivate') || 'Réactiver'}`}
                                  </button>
                                  <button
                                    onClick={() => handleDeleteFormation(formation.id)}
                                    style={mobileStyles.button('danger')}
                                  >
                                    🗑️ {t('common.delete')}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  ) : (
                    // Version desktop avec table
                    <div style={mobileStyles.tableWrapper}>
                      <table style={mobileStyles.table}>
                        <thead>
                          <tr>
                            <th style={mobileStyles.th}>{t('formations.title')}</th>
                            <th style={mobileStyles.th}>{t('formations.category')}</th>
                            <th style={mobileStyles.th}>{t('formations.details') || 'Détails'}</th>
                            <th style={mobileStyles.th}>{t('formations.enrolled') || 'Inscrits'}</th>
                            <th style={mobileStyles.th}>{t('formations.cost')}</th>
                            <th style={mobileStyles.th}>{t('common.actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formations.map((formation) => (
                            <tr key={formation.id} style={{ background: formation.is_active ? 'white' : '#f9fafb', opacity: formation.is_active ? 1 : 0.75 }}>
                              <td style={mobileStyles.td}>
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                    <h4 style={{ fontSize: '0.875rem', fontWeight: '500', color: '#111827', margin: 0 }}>
                                      {formation.title}
                                    </h4>
                                    <span style={mobileStyles.badge(
                                      formation.is_mandatory ? '#dc2626' : '#059669',
                                      formation.is_mandatory ? '#fee2e2' : '#dcfce7'
                                    )}>
                                      {formation.is_mandatory ? t('formations.mandatory') : t('formations.optional')}
                                    </span>
                                    {!formation.is_active && (
                                      <span style={mobileStyles.badge('#6b7280', '#f3f4f6')}>
                                        {t('formations.archived') || 'Archivée'}
                                      </span>
                                    )}
                                  </div>
                                  <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '4px 0' }}>
                                    {formation.description}
                                  </p>
                                  {formation.target_departments && (
                                    <p style={{ fontSize: '0.75rem', color: '#2563eb', margin: '4px 0' }}>
                                      {t('formations.departments_label')} : {formation.target_departments}
                                    </p>
                                  )}
                                </div>
                              </td>
                              <td style={mobileStyles.td}>
                                <span style={{ fontSize: '0.875rem', color: '#111827' }}>{formation.category}</span>
                              </td>
                              <td style={mobileStyles.td}>
                                <div style={{ fontSize: '0.875rem', color: '#6b7280', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  {formation.duration_hours && <div>⏱️ {formation.duration_hours}h</div>}
                                  {formation.trainer && <div>👨‍🏫 {formation.trainer}</div>}
                                  {formation.location && <div>📍 {formation.location}</div>}
                                  {formation.start_date && <div>📅 {new Date(formation.start_date).toLocaleDateString(navigator.language || 'fr-FR')}</div>}
                                </div>
                              </td>
                              <td style={mobileStyles.td}>
                                <div style={{ fontSize: '0.875rem', color: '#111827' }}>
                                  {formation.enrolled_count || 0}
                                  {formation.max_participants && ` / ${formation.max_participants}`}
                                </div>
                                {formation.pending_count > 0 && (
                                  <div style={{ fontSize: '0.75rem', color: '#d97706' }}>
                                    {t('formations.pending_count', { count: formation.pending_count })}
                                  </div>
                                )}
                              </td>
                              <td style={mobileStyles.td}>
                                <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#111827' }}>
                                  {formation.cost_per_person > 0 ? `${formation.cost_per_person}€` : t('formations.free')}
                                </span>
                              </td>
                              <td style={mobileStyles.td}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                  {user?.role !== 'admin' && formation.is_active && (
                                    <button
                                      onClick={() => {
                                        const justification = prompt(t('formations.justification_prompt'));
                                        if (justification) {
                                          handleInscription(formation.id, justification);
                                        }
                                      }}
                                      style={mobileStyles.button(
                                        formation.current_user_enrolled || (formation.max_participants && formation.enrolled_count >= formation.max_participants)
                                          ? 'gray' : 'primary'
                                      )}
                                      disabled={formation.current_user_enrolled || (formation.max_participants && formation.enrolled_count >= formation.max_participants)}
                                    >
                                      {formation.current_user_enrolled
                                        ? `✓ ${t('formations.already_registered') || 'Déjà inscrit'}`
                                        : formation.max_participants && formation.enrolled_count >= formation.max_participants
                                        ? `🚫 ${t('formations.full') || 'Complet'}`
                                        : `📝 ${t('formations.inscribe')}`
                                      }
                                    </button>
                                  )}
                                  
                                  {['admin', 'manager'].includes(user?.role) && (
                                    <>
                                      <button
                                        onClick={() => handleEditFormation(formation)}
                                        style={mobileStyles.button('secondary')}
                                      >
                                        ✏️ {t('formations.edit')}
                                      </button>
                                      <button
                                        onClick={() => handleArchiveFormation(formation.id, formation.is_active)}
                                        style={mobileStyles.button(formation.is_active ? 'secondary' : 'success')}
                                      >
                                        {formation.is_active ? `📦 ${t('formations.archive')}` : `🔄 ${t('formations.reactivate')}`}
                                      </button>
                                      <button
                                        onClick={() => handleDeleteFormation(formation.id)}
                                        style={mobileStyles.button('danger')}
                                      >
                                        🗑️ {t('common.delete')}
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {formations.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '48px 16px' }}>
                          <div style={{ fontSize: '4rem', marginBottom: '16px' }}>📚</div>
                          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                            {t('formations.no_formations')}
                          </h3>
                          <p style={{ color: '#6b7280' }}>{t('formations.catalog_update_soon')}</p>
                        </div>
                      )}
                    </div>
                  )}
              </ModernCard>
            </div>
          )}

          {/* CRÉER/MODIFIER FORMATION (Admin) */}
          {activeTab === 'creer-formation' && ['admin', 'manager'].includes(user?.role) && (
            <ModernCard
              title={editingFormation ? t('formations.edit_formation') : t('formations.create_formation')}
              icon={<Plus size={20} />}
              gradient={colorPalette.formations.gradient}
              shadow={colorPalette.formations.shadow}
            >
                <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginTop: '8px', marginBottom: '24px' }}>
                  {editingFormation ? t('formations.edit_info') || 'Modifiez les informations de la formation' : t('formations.fill_info') || 'Remplissez les informations de la nouvelle formation'}
                </p>

                <div style={{ padding: isMobile ? '16px' : '24px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '20px' : '24px' }}>
                    {/* Informations de base */}
                    <div style={mobileStyles.formGrid}>
                      <div style={{ gridColumn: isMobile ? '1' : 'span 2' }}>
                        <div style={mobileStyles.formGroup}>
                          <label style={mobileStyles.label}>{t('formations.title_field')} *</label>
                          <input
                            type="text"
                            value={newFormation.title}
                            onChange={(e) => setNewFormation(prev => ({ ...prev, title: e.target.value }))}
                            style={mobileStyles.input}
                            placeholder={t('formations.title_placeholder') || 'Ex: Formation Excel Avancé'}
                          />
                        </div>
                      </div>

                      <div style={mobileStyles.formGroup}>
                        <label style={mobileStyles.label}>{t('formations.category')} *</label>
                        <select
                          value={newFormation.category}
                          onChange={(e) => setNewFormation(prev => ({ ...prev, category: e.target.value }))}
                          style={mobileStyles.input}
                        >
                          <option value="">{t('formations.select_category') || 'Sélectionner une catégorie'}</option>
                          {categories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>

                      <div style={mobileStyles.formGroup}>
                        <label style={mobileStyles.label}>{t('formations.duration_hours')}</label>
                        <input
                          type="number"
                          step="0.5"
                          value={newFormation.duration_hours}
                          onChange={(e) => setNewFormation(prev => ({ ...prev, duration_hours: e.target.value }))}
                          style={mobileStyles.input}
                          placeholder={t('formations.duration_placeholder') || 'Ex: 8'}
                        />
                      </div>

                      <div style={mobileStyles.formGroup}>
                        <label style={mobileStyles.label}>{t('formations.trainer')}</label>
                        <input
                          type="text"
                          value={newFormation.trainer}
                          onChange={(e) => setNewFormation(prev => ({ ...prev, trainer: e.target.value }))}
                          style={mobileStyles.input}
                          placeholder={t('formations.trainer_placeholder') || 'Nom du formateur ou organisme'}
                        />
                      </div>

                      <div style={mobileStyles.formGroup}>
                        <label style={mobileStyles.label}>{t('formations.location')}</label>
                        <input
                          type="text"
                          value={newFormation.location}
                          onChange={(e) => setNewFormation(prev => ({ ...prev, location: e.target.value }))}
                          style={mobileStyles.input}
                          placeholder={t('formations.location_placeholder') || 'Salle de formation, Distanciel, Externe...'}
                        />
                      </div>

                      <div style={mobileStyles.formGroup}>
                        <label style={mobileStyles.label}>{t('formations.max_participants')}</label>
                        <input
                          type="number"
                          value={newFormation.max_participants}
                          onChange={(e) => setNewFormation(prev => ({ ...prev, max_participants: e.target.value }))}
                          style={mobileStyles.input}
                          placeholder={t('formations.max_participants_placeholder') || 'Ex: 15'}
                        />
                      </div>

                      <div style={mobileStyles.formGroup}>
                        <label style={mobileStyles.label}>{t('formations.cost_per_person')} (€)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={newFormation.cost_per_person}
                          onChange={(e) => setNewFormation(prev => ({ ...prev, cost_per_person: e.target.value }))}
                          style={mobileStyles.input}
                          placeholder="0.00"
                        />
                      </div>

                      <div style={mobileStyles.formGroup}>
                        <label style={mobileStyles.label}>{t('formations.start_date')}</label>
                        <input
                          type="datetime-local"
                          value={newFormation.start_date}
                          onChange={(e) => setNewFormation(prev => ({ ...prev, start_date: e.target.value }))}
                          style={mobileStyles.input}
                        />
                      </div>

                      <div style={mobileStyles.formGroup}>
                        <label style={mobileStyles.label}>{t('formations.end_date')}</label>
                        <input
                          type="datetime-local"
                          value={newFormation.end_date}
                          onChange={(e) => setNewFormation(prev => ({ ...prev, end_date: e.target.value }))}
                          style={mobileStyles.input}
                        />
                      </div>

                      <div style={mobileStyles.formGroup}>
                        <label style={mobileStyles.label}>{t('formations.registration_deadline') || 'Date limite d\'inscription'}</label>
                        <input
                          type="datetime-local"
                          value={newFormation.registration_deadline}
                          onChange={(e) => setNewFormation(prev => ({ ...prev, registration_deadline: e.target.value }))}
                          style={mobileStyles.input}
                        />
                      </div>
                    </div>

                    {/* Départements concernés */}
                    <div>
                      <label style={mobileStyles.label}>{t('formations.target_departments') || 'Départements concernés'}</label>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                        gap: '12px'
                      }}>
                        {departments.map(dept => (
                          <label key={dept} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="checkbox"
                              checked={newFormation.target_departments.includes(dept)}
                              onChange={() => handleDepartmentToggle(dept)}
                              style={{ borderRadius: '4px' }}
                            />
                            <span style={{ fontSize: '0.875rem', color: '#374151' }}>{dept}</span>
                          </label>
                        ))}
                      </div>
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '8px' }}>
                        {t('formations.departments_info') || 'Si aucun département n\'est sélectionné, la formation sera ouverte à tous'}
                      </p>
                    </div>

                    {/* Description */}
                    <div style={mobileStyles.formGroup}>
                      <label style={mobileStyles.label}>{t('formations.description')} *</label>
                      <textarea
                        value={newFormation.description}
                        onChange={(e) => setNewFormation(prev => ({ ...prev, description: e.target.value }))}
                        rows="3"
                        style={mobileStyles.textarea}
                        placeholder={t('formations.description_placeholder') || 'Description détaillée de la formation...'}
                      />
                    </div>

                    <div style={mobileStyles.formGroup}>
                      <label style={mobileStyles.label}>{t('formations.objectives') || 'Objectifs pédagogiques'}</label>
                      <textarea
                        value={newFormation.objectives}
                        onChange={(e) => setNewFormation(prev => ({ ...prev, objectives: e.target.value }))}
                        rows="3"
                        style={mobileStyles.textarea}
                        placeholder={t('formations.objectives_placeholder') || 'À l\'issue de cette formation, les participants seront capables de...'}
                      />
                    </div>

                    <div style={mobileStyles.formGroup}>
                      <label style={mobileStyles.label}>{t('formations.prerequisites') || 'Prérequis'}</label>
                      <textarea
                        value={newFormation.prerequisites}
                        onChange={(e) => setNewFormation(prev => ({ ...prev, prerequisites: e.target.value }))}
                        rows="2"
                        style={mobileStyles.textarea}
                        placeholder={t('formations.prerequisites_placeholder') || 'Connaissances ou compétences requises...'}
                      />
                    </div>

                    {/* Options */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          checked={newFormation.is_mandatory}
                          onChange={(e) => setNewFormation(prev => ({ ...prev, is_mandatory: e.target.checked }))}
                          style={{ borderRadius: '4px' }}
                        />
                        <span style={{ fontSize: '0.875rem', color: '#374151' }}>{t('formations.mandatory')}</span>
                      </label>

                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          checked={newFormation.certification_provided}
                          onChange={(e) => setNewFormation(prev => ({ ...prev, certification_provided: e.target.checked }))}
                          style={{ borderRadius: '4px' }}
                        />
                        <span style={{ fontSize: '0.875rem', color: '#374151' }}>{t('formations.certification_provided') || 'Certification fournie'}</span>
                      </label>
                    </div>

                    {/* Public cible - Admin uniquement (Manager = toujours "Tous les employés") */}
                    {user?.role === 'admin' && (
                      <div style={{ marginTop: '16px' }}>
                        <label style={mobileStyles.label}>{t('formations.target_audience') || 'Public cible'}</label>
                        <select
                          value={newFormation.target_audience || 'all'}
                          onChange={(e) => setNewFormation(prev => ({ ...prev, target_audience: e.target.value }))}
                          style={{ ...mobileStyles.input, marginTop: '6px' }}
                        >
                          <option value="all">{t('formations.target_all') || 'Tous les employés'}</option>
                          <option value="managers_only">{t('formations.target_managers') || 'Managers uniquement'}</option>
                        </select>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '4px' }}>
                          {t('formations.target_audience_help') || 'Managers uniquement = visible par admin/manager seulement'}
                        </p>
                      </div>
                    )}
                    {user?.role === 'manager' && (
                      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '12px' }}>
                        {t('formations.manager_creates_all') || 'Les formations que vous créez sont visibles par tous les employés.'}
                      </p>
                    )}

                    {/* Actions */}
                    <div style={{
                      display: 'flex',
                      flexDirection: isMobile ? 'column' : 'row',
                      justifyContent: 'flex-end',
                      gap: '12px',
                      paddingTop: '24px',
                      borderTop: '1px solid #e5e7eb'
                    }}>
                      <button
                        onClick={() => {
                          resetForm();
                          setActiveTab('catalogue');
                        }}
                        style={{
                          ...mobileStyles.button('gray'),
                          background: '#f3f4f6',
                          color: '#374151'
                        }}
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        onClick={handleCreateFormation}
                        disabled={!isFormValid}
                        style={{
                          ...mobileStyles.button('primary'),
                          opacity: isFormValid ? 1 : 0.5,
                          cursor: isFormValid ? 'pointer' : 'not-allowed'
                        }}
                      >
                        {editingFormation ? `✅ ${t('formations.edit_formation')}` : `✅ ${t('formations.create_formation')}`}
                      </button>
                    </div>
                  </div>
                </div>
            </ModernCard>
          )}

          {/* MES INSCRIPTIONS - Table: Employee, Course, Dates */}
          {activeTab === 'mes-inscriptions' && (
            <ModernCard
              title={t('formations.my_inscriptions')}
              icon={<FileCheck size={20} />}
              gradient={colorPalette.formations.gradient}
              shadow={colorPalette.formations.shadow}
            >
                <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '16px', overflowX: 'auto' }}>
                  {inscriptions.length > 0 && (
                    <div style={{
                      background: '#ffffff',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                    }}>
                      {!isMobile ? (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left', background: '#f8fafc' }}>
                              <th style={{ padding: '12px 8px', color: '#374151', fontWeight: '600' }}>{t('formations.employee_name') || 'Employé'}</th>
                              <th style={{ padding: '12px 8px', color: '#374151', fontWeight: '600' }}>{t('formations.course_name') || 'Formation'}</th>
                              <th style={{ padding: '12px 8px', color: '#374151', fontWeight: '600' }}>{t('formations.dates') || 'Dates'}</th>
                              <th style={{ padding: '12px 8px', color: '#374151', fontWeight: '600' }}>{t('formations.requested_on') || 'Demandé le'}</th>
                              <th style={{ padding: '12px 8px', color: '#374151', fontWeight: '600' }}>{t('formations.status') || 'Statut'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {inscriptions.map((inscription) => (
                              <tr key={inscription.id} style={{ borderBottom: '1px solid #e5e7eb', background: '#ffffff' }}>
                                <td style={{ padding: '12px 8px', color: '#111827' }}>
                                  {[inscription.first_name, inscription.last_name].filter(Boolean).join(' ') || inscription.employee_code || '—'}
                                  {inscription.department && (
                                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{inscription.department}</div>
                                  )}
                                </td>
                                <td style={{ padding: '12px 8px', color: '#111827' }}>
                                  <strong>{inscription.title}</strong>
                                  {inscription.category && (
                                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{inscription.category}</div>
                                  )}
                                </td>
                                <td style={{ padding: '12px 8px', color: '#334155' }}>
                                  {inscription.start_date && new Date(inscription.start_date).toLocaleDateString(navigator.language || 'fr-FR')}
                                  {inscription.end_date && inscription.start_date !== inscription.end_date && (
                                    <> → {new Date(inscription.end_date).toLocaleDateString(navigator.language || 'fr-FR')}</>
                                  )}
                                  {!inscription.start_date && '—'}
                                </td>
                                <td style={{ padding: '12px 8px', color: '#475569' }}>
                                  {inscription.requested_at && new Date(inscription.requested_at).toLocaleDateString(navigator.language || 'fr-FR')}
                                </td>
                                <td style={{ padding: '12px 8px' }}>
                                  <span
                                    style={{
                                      padding: '4px 10px',
                                      fontSize: '0.75rem',
                                      fontWeight: '600',
                                      borderRadius: '12px',
                                      backgroundColor: getStatusColor(inscription.status) + '22',
                                      color: getStatusColor(inscription.status)
                                    }}
                                  >
                                    {getStatusText(inscription.status)}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        inscriptions.map((inscription) => (
                          <div key={inscription.id} style={{
                            padding: '16px',
                            borderBottom: '1px solid #e5e7eb',
                            display: 'grid',
                            gap: '8px',
                            background: '#ffffff',
                            color: '#111827'
                          }}>
                            <div><strong style={{ color: '#374151' }}>{t('formations.employee_name') || 'Employé'}:</strong> <span style={{ color: '#111827' }}>{[inscription.first_name, inscription.last_name].filter(Boolean).join(' ') || inscription.employee_code || '—'}</span></div>
                            <div><strong style={{ color: '#374151' }}>{t('formations.course_name') || 'Formation'}:</strong> <span style={{ color: '#111827' }}>{inscription.title} {inscription.category && `(${inscription.category})`}</span></div>
                            <div><strong style={{ color: '#374151' }}>{t('formations.dates') || 'Dates'}:</strong> <span style={{ color: '#334155' }}>{inscription.start_date ? new Date(inscription.start_date).toLocaleDateString(navigator.language || 'fr-FR') : '—'}
                              {inscription.end_date && inscription.start_date !== inscription.end_date && ` → ${new Date(inscription.end_date).toLocaleDateString(navigator.language || 'fr-FR')}`}
                            </span></div>
                            <div><strong style={{ color: '#374151' }}>{t('formations.requested_on') || 'Demandé le'}:</strong> <span style={{ color: '#475569' }}>{inscription.requested_at && new Date(inscription.requested_at).toLocaleDateString(navigator.language || 'fr-FR')}</span></div>
                            <div>
                              <span
                                style={{
                                  padding: '4px 10px',
                                  fontSize: '0.75rem',
                                  fontWeight: '600',
                                  borderRadius: '12px',
                                  backgroundColor: getStatusColor(inscription.status) + '22',
                                  color: getStatusColor(inscription.status)
                                }}
                              >
                                {getStatusText(inscription.status)}
                              </span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                {inscriptions.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '48px 16px' }}>
                    <div style={{ fontSize: '4rem', marginBottom: '16px' }}>📋</div>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#ffffff', marginBottom: '8px' }}>
                      {t('formations.no_inscriptions') || 'Aucune inscription'}
                    </h3>
                    <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{t('formations.no_inscriptions_info') || 'Vous n\'avez pas encore d\'inscription aux formations'}</p>
                  </div>
                )}
            </div>
            </ModernCard>
          )}

          {/* VALIDATION (Admin + Manager) */}
          {activeTab === 'validation' && ['admin', 'manager'].includes(user?.role) && (
            <ModernCard
              title={`${t('formations.pending_validations') || 'Inscriptions à valider'} (${pendingInscriptions.length})`}
              icon={<CheckCircle size={20} />}
              gradient={colorPalette.formations.gradient}
              shadow={colorPalette.formations.shadow}
            >
                <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '16px' }}>
                  {pendingInscriptions.map((inscription) => (
                    <div key={inscription.id} style={{
                      padding: isMobile ? '16px' : '24px',
                      borderBottom: '1px solid #e5e7eb',
                      background: '#ffffff',
                      borderRadius: '12px',
                      marginBottom: '12px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                    }}>
                      <div style={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: 'space-between',
                        alignItems: isMobile ? 'stretch' : 'flex-start',
                        gap: '16px'
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            alignItems: isMobile ? 'flex-start' : 'center',
                            gap: '12px',
                            marginBottom: '12px'
                          }}>
                            <h3 style={{
                              fontSize: isMobile ? '1rem' : '1.125rem',
                              fontWeight: '500',
                              color: '#111827',
                              margin: 0
                            }}>
                              {inscription.title}
                            </h3>
                            <span style={{ fontSize: '0.875rem', color: '#475569' }}>
                              ({inscription.category})
                            </span>
                          </div>

                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                            gap: '16px',
                            marginBottom: '12px'
                          }}>
                            <div>
                              <p style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                                {t('formations.employee')}
                              </p>
                              <p style={{ fontSize: '0.875rem', color: '#334155' }}>
                                {inscription.first_name} {inscription.last_name} ({inscription.employee_code})
                              </p>
                              <p style={{ fontSize: '0.75rem', color: '#64748b' }}>{inscription.department}</p>
                            </div>
                            <div>
                              <p style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                                {t('formations.training')}
                              </p>
                              {inscription.start_date && (
                                <p style={{ fontSize: '0.875rem', color: '#334155' }}>
                                  📅 {new Date(inscription.start_date).toLocaleDateString(navigator.language || 'fr-FR')}
                                </p>
                              )}
                              {inscription.cost_per_person > 0 && (
                                <p style={{ fontSize: '0.875rem', color: '#334155' }}>
                                  💰 {inscription.cost_per_person}€
                                </p>
                              )}
                            </div>
                          </div>

                          {inscription.justification && (
                            <div style={{
                              padding: '12px',
                              background: '#dbeafe',
                              borderRadius: '8px',
                              marginBottom: '16px'
                            }}>
                              <p style={{ fontSize: '0.875rem', color: '#1e40af' }}>
                                <strong>{t('formations.justification_label')} :</strong> {inscription.justification}
                              </p>
                            </div>
                          )}

                          <p style={{ fontSize: '0.75rem', color: '#475569' }}>
                            {t('formations.requested_on')} {new Date(inscription.requested_at).toLocaleDateString(navigator.language || 'fr-FR')}
                          </p>
                        </div>

                        <div style={{
                          display: 'flex',
                          flexDirection: isMobile ? 'row' : 'row',
                          gap: '8px'
                        }}>
                          <button
                            onClick={() => handleValidation(inscription.id, 'approve')}
                            style={mobileStyles.button('success')}
                          >
                            ✅ {t('formations.approve')}
                          </button>
                          <button
                            onClick={() => {
                              const reason = prompt(t('formations.rejection_reason_prompt'));
                              if (reason) {
                                handleValidation(inscription.id, 'reject', reason);
                              }
                            }}
                            style={mobileStyles.button('danger')}
                          >
                            ❌ {t('formations.reject')}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {pendingInscriptions.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '48px 16px' }}>
                    <div style={{ fontSize: '4rem', marginBottom: '16px' }}>✅</div>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#ffffff', marginBottom: '8px' }}>
                      {t('formations.no_pending_validations') || 'Aucune validation en attente'}
                    </h3>
                    <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{t('formations.all_processed') || 'Toutes les demandes ont été traitées'}</p>
                  </div>
                )}
            </ModernCard>
          )}

          {/* INSCRITS (Admin + Manager) - Liste des inscrits par formation */}
          {activeTab === 'inscrits' && ['admin', 'manager'].includes(user?.role) && (
            <ModernCard
              title={t('formations.registered_users') || 'Inscrits'}
              icon={<Users size={20} />}
              gradient={colorPalette.formations.gradient}
              shadow={colorPalette.formations.shadow}
            >
              <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '16px' }}>
                {(() => {
                  const flatRows = formations
                    .filter(f => (f.enrolled_list?.length || 0) > 0)
                    .flatMap(f => (f.enrolled_list || []).map((reg, idx) => ({
                      key: `${f.id}-${idx}-${reg.employee_code || idx}`,
                      first_name: reg.first_name,
                      last_name: reg.last_name,
                      employee_code: reg.employee_code,
                      department: reg.department,
                      status: reg.status,
                      courseTitle: f.title,
                      courseCategory: f.category,
                      start_date: f.start_date,
                      end_date: f.end_date
                    })));
                  if (flatRows.length === 0) {
                    return (
                      <div style={{ textAlign: 'center', padding: '48px 16px' }}>
                        <div style={{ fontSize: '4rem', marginBottom: '16px' }}>👥</div>
                        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#ffffff', marginBottom: '8px' }}>
                          {t('formations.no_enrolled_yet') || 'Aucun inscrit pour le moment'}
                        </h3>
                        <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{t('formations.no_enrolled_info') || 'Les employés inscrits aux formations apparaîtront ici'}</p>
                      </div>
                    );
                  }
                  const getStatusStyle = (status) => ({
                    padding: '4px 10px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    borderRadius: '12px',
                    backgroundColor: status === 'pending' ? '#fef3c7' : status === 'approved' ? '#d1fae5' : status === 'rejected' ? '#fee2e2' : '#f1f5f9',
                    color: status === 'pending' ? '#b45309' : status === 'approved' ? '#047857' : status === 'rejected' ? '#b91c1c' : '#475569'
                  });
                  return (
                    <div style={{ background: '#ffffff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                      {!isMobile ? (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left', background: '#f8fafc' }}>
                              <th style={{ padding: '12px 10px', color: '#374151', fontWeight: '600' }}>{t('formations.employee_name') || 'Employé'}</th>
                              <th style={{ padding: '12px 10px', color: '#374151', fontWeight: '600' }}>{t('formations.course_name') || 'Formation'}</th>
                              <th style={{ padding: '12px 10px', color: '#374151', fontWeight: '600' }}>{t('formations.dates') || 'Dates'}</th>
                              <th style={{ padding: '12px 10px', color: '#374151', fontWeight: '600' }}>{t('formations.status') || 'Statut'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {flatRows.map((row) => (
                              <tr key={row.key} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                <td style={{ padding: '12px 10px', color: '#111827' }}>
                                  <div style={{ fontWeight: '600' }}>{[row.first_name, row.last_name].filter(Boolean).join(' ') || row.employee_code || '—'}</div>
                                  {(row.employee_code || row.department) && (
                                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>{[row.employee_code, row.department].filter(Boolean).join(' • ')}</div>
                                  )}
                                </td>
                                <td style={{ padding: '12px 10px', color: '#111827' }}>
                                  <div style={{ fontWeight: '500' }}>{row.courseTitle}</div>
                                  {row.courseCategory && <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{row.courseCategory}</div>}
                                </td>
                                <td style={{ padding: '12px 10px', color: '#334155' }}>
                                  {row.start_date ? new Date(row.start_date).toLocaleDateString(navigator.language || 'fr-FR') : '—'}
                                  {row.end_date && row.start_date !== row.end_date && (
                                    <> → {new Date(row.end_date).toLocaleDateString(navigator.language || 'fr-FR')}</>
                                  )}
                                </td>
                                <td style={{ padding: '12px 10px' }}>
                                  <span style={getStatusStyle(row.status)}>
                                    {row.status === 'pending' ? t('formations.status_pending') : row.status === 'approved' ? t('formations.status_approved') : row.status === 'rejected' ? t('formations.status_rejected') : row.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div style={{ padding: '12px' }}>
                          {flatRows.map((row) => (
                            <div
                              key={row.key}
                              style={{
                                padding: '14px',
                                borderBottom: '1px solid #e5e7eb',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px'
                              }}
                            >
                              <div>
                                <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '600', marginBottom: '2px' }}>{t('formations.employee_name') || 'Employé'}</div>
                                <div style={{ color: '#111827' }}>{[row.first_name, row.last_name].filter(Boolean).join(' ') || row.employee_code || '—'}</div>
                                {(row.employee_code || row.department) && (
                                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>{[row.employee_code, row.department].filter(Boolean).join(' • ')}</div>
                                )}
                              </div>
                              <div>
                                <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '600', marginBottom: '2px' }}>{t('formations.course_name') || 'Formation'}</div>
                                <div style={{ color: '#111827' }}>{row.courseTitle}</div>
                                {row.courseCategory && <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{row.courseCategory}</div>}
                              </div>
                              <div>
                                <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '600', marginBottom: '2px' }}>{t('formations.dates') || 'Dates'}</div>
                                <div style={{ color: '#334155' }}>
                                  {row.start_date ? new Date(row.start_date).toLocaleDateString(navigator.language || 'fr-FR') : '—'}
                                  {row.end_date && row.start_date !== row.end_date && ` → ${new Date(row.end_date).toLocaleDateString(navigator.language || 'fr-FR')}`}
                                </div>
                              </div>
                              <div>
                                <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '600', marginBottom: '4px' }}>{t('formations.status') || 'Statut'}</div>
                                <span style={getStatusStyle(row.status)}>
                                  {row.status === 'pending' ? t('formations.status_pending') : row.status === 'approved' ? t('formations.status_approved') : row.status === 'rejected' ? t('formations.status_rejected') : row.status}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </ModernCard>
          )}
        </div>
        )}
      </div>

      {/* CSS pour animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (min-width: 768px) {
          .formations-content {
            padding: 120px 40px 40px !important;
          }
        }
        @media (min-width: 1024px) {
          .formations-content {
            padding: 140px 60px 60px !important;
          }
        }
        .formations-content {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default FormationsManagement;